import { computed, DestroyRef, inject, Injectable, signal } from "@angular/core";
import { SidenavModel } from "../models/sidenav.model";
import { SidenavItemInterface } from "../interfaces/side-nav-items.interface";
import { AuthService } from "../../../../kernel/tools/services/auth.service";
import { PRIMARY_OUTLET, Router } from "@angular/router";
import { tap } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class SideNavService {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private authService = inject(AuthService);

  private _mainLinks = signal<SidenavModel[]>([]);

  private _selectedMainLink = signal(this._mainLinks().find(link => link.expanded));

  selectedMainLink = computed(() => this._selectedMainLink());

  private _rootSidenavItem: SidenavModel = new SidenavModel({
    id: "root",
    items: [],
    label: "ROOT",
    icon: "",
  });

  get rootSidenavItem() {
    return this._rootSidenavItem;
  }

  mainLinks = computed<SidenavModel[]>(() => this._mainLinks());

  constructor() {
    this.checkPermissions();
  }

  /**
   * Observe user to check permissions and update main links in sidenav
   */
  observeUserAuthorization() {
    return this.authService.user$.pipe(
      tap(() => {
        this.checkPermissions();
        this.updateLinksState();
      })
    );
  }

  updateLinksState() {
    this.rootSidenavItem.unExpandAllItems();
    this.searchMenuActivatedRoute(this.router.url);
  }

  checkPermissions() {
    const user = this.authService.getUser();
    this._mainLinks().map(link => link.checkPermissions(user));
  }

  /**
   * This will reset selectedMainLink (after user logout).
   * TODO: Refactor sidenav to be called once in App root
   */
  resetSideNav() {
    this._selectedMainLink()?.unExpandAllItems();
    this._selectedMainLink.set(undefined);
  }

  /**
   * Set Main links
   * @param mainLinks
   */
  setMainLinks(mainLinks: SidenavItemInterface[]): void {
    this._mainLinks.set(this.rootSidenavItem.setMainLinks(mainLinks));
    this._mainLinks.update(links => links.map(i => i.checkPermissions(this.authService.getUser())));
  }

  /**
   * set selected main link
   * @param id
   * @param routerLink
   */
  setSelectedMainLink(id?: string | null, routerLink?: string | string[]): void {
    let selectedMainLink: SidenavModel | undefined;
    if (id) {
      selectedMainLink = this.setSelectMainItemWithId(id);
    } else if (routerLink) {
      routerLink = Array.isArray(routerLink) ? routerLink : routerLink?.split("/");
      selectedMainLink = this.setSelectMainItemWithRouterLink(routerLink);
    }
    this._selectedMainLink.set(selectedMainLink);
  }

  /**
   * set selected main link if it isShown and expanded
   * if not expanded set expanded: true for first element has isShown
   * @private
   */
  private setSelectMainItemWithRouterLink(routerLink: string[]): SidenavModel | undefined {
    let selectedMainLink = this._mainLinks().find(link => {
      if (link.getMatchRate(routerLink)) {
        return true;
      } else {
        return link.isShown && link.expanded;
      }
    });
    if (!selectedMainLink) {
      selectedMainLink = this._mainLinks().find(link => link.isShown);
      selectedMainLink && (selectedMainLink.expanded = true);
    }
    return selectedMainLink;
  }

  /**
   * Select Main item with ID
   * @param id
   * @private
   */
  private setSelectMainItemWithId(id: string): SidenavModel | undefined {
    const selectedMainLink = this._mainLinks().find(link => link.id === id);
    if (selectedMainLink?.routerLink) this.rootSidenavItem.unExpandAllItems();
    selectedMainLink?.expandItem(!!selectedMainLink?.routerLink);
    return selectedMainLink;
  }

  /**
   * Add sub menu items
   * @param id
   * @param items
   */
  addSubMenuItems(id: string, items: SidenavItemInterface[]): void {
    const mainLink = this._mainLinks().find(link => link.id === id);
    mainLink?.setSubMainLinks(items).checkPermissions(this.authService.getUser());
    this._mainLinks.set(this._mainLinks());
  }

  /**
   * expand an item or activate router link
   * @param item
   */
  toggleExpandItem(item: SidenavModel) {
    if (item.expanded) return item.unExpandAllItems();
    if (item.routerLink) this.rootSidenavItem.unExpandAllItems();
    else item.parent?.unExpandAllItems();
    item.expandItem(!!item.routerLink);
  }

  /**
   * Check for current url and compare all sidenav items with it
   * @param routerLink
   */
  searchMenuActivatedRoute(routerLink: string | string[]): void {
    // const routeArray = Array.isArray(routerLink) ? routerLink : routerLink.split("/");
    let routeArray: string[];
    if (Array.isArray(routerLink)) {
      routeArray = routerLink;
    } else {
      const urlTree = this.router.parseUrl(routerLink);
      const primarySegments = urlTree.root.children[PRIMARY_OUTLET]?.segments || [];
      const path = "/" + primarySegments.map(s => s.path).join("/");
      routeArray = path.split("/");
    }
    this.rootSidenavItem.activateItem(routeArray);
    this.setSelectedMainLink(undefined, routeArray);
  }
}
